import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { StateService } from "../../../services/state.service";
import { UserStore } from "../../../stores/user.store";
import { ExperienceListItem, TrailApi } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { formatUserprefDate } from "./trail.userpref.date";

type StatusFilter = "" | "PENDING" | "APPROVED" | "DENIED";

@TypeManagerDecorator("s25-ng-trail-experience-admin")
@Component({
    selector: "s25-ng-trail-experience-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ButtonComponent, S25LoadingInlineStaticComponent],
    host: { class: "trail-experience-admin" },
    template: `
        <div class="c-padding--double">
            <div class="trail-experience-admin__header c-margin-bottom--single">
                <h1>{{ labels.REVIEW_EXPERIENCES }}</h1>
                <s25-ng-button data-id="back" [type]="'outline'" [onClick]="handleBack"> Back to Admin </s25-ng-button>
            </div>

            <div class="trail-experience-admin__filters c-margin-bottom--single">
                <div class="trail-experience-admin__filter">
                    <label for="admin-cohort-filter">{{ labels.COHORT }}:</label>
                    <select
                        id="admin-cohort-filter"
                        class="c-input"
                        [ngModel]="selectedCohortId()"
                        (ngModelChange)="selectedCohortId.set($event)"
                    >
                        <option [ngValue]="null">Select {{ labels.COHORT.toLowerCase() }}</option>
                        @for (cohort of cohorts.value() ?? []; track cohort.cohortId) {
                            <option [ngValue]="cohort.cohortId">{{ cohort.cohortCode }}</option>
                        }
                    </select>
                </div>

                <div class="trail-experience-admin__filter">
                    <label for="admin-curric-filter">{{ labels.CURRICULUM }}:</label>
                    <select
                        id="admin-curric-filter"
                        class="c-input"
                        [ngModel]="selectedDefnId()"
                        (ngModelChange)="selectedDefnId.set($event)"
                        [disabled]="selectedCohortId() == null"
                    >
                        @if (selectedCohortId() == null) {
                            <option [ngValue]="null">Select a {{ labels.COHORT.toLowerCase() }} first</option>
                        } @else {
                            <option [ngValue]="null">All {{ labels.CURRICULA }}</option>
                            @for (curriculum of curricula.value() ?? []; track curriculum.defnId) {
                                <option [ngValue]="curriculum.defnId">
                                    {{ curriculum.displayName || curriculum.name }}
                                </option>
                            }
                        }
                    </select>
                </div>

                <div class="trail-experience-admin__filter">
                    <label for="admin-status-filter">Status:</label>
                    <select
                        id="admin-status-filter"
                        class="c-input"
                        [ngModel]="statusFilter()"
                        (ngModelChange)="statusFilter.set($event)"
                    >
                        <option value="">All</option>
                        <option value="PENDING">{{ labels.APPROVAL_PENDING }}</option>
                        <option value="APPROVED">{{ labels.APPROVED }}</option>
                        <option value="DENIED">{{ labels.DENIED }}</option>
                    </select>
                </div>
                <s25-ng-button data-id="refresh-list" [type]="'outline'" [onClick]="refreshList">
                    Refresh
                </s25-ng-button>
            </div>

            @if (!selectedCohortId()) {
                <p class="c-text--secondary">
                    Select a {{ labels.COHORT.toLowerCase() }} first, then a {{ labels.CURRICULUM.toLowerCase() }}.
                </p>
            } @else if (experiencesRes.isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading experiences…'" />
            } @else if (experiencesRes.error()) {
                <p class="c-text--error">Unable to load experiences.</p>
            } @else if (!experiences().length) {
                <p class="c-text--secondary">{{ labels.NO_EXPERIENCES }}</p>
            } @else {
                <div class="trail-experience-admin__cards">
                    @for (experience of experiences(); track experience.experienceId) {
                        <div
                            class="trail-experience-admin__card"
                            [class.trail-experience-admin__card--pending]="experience.status === 'PENDING'"
                            [class.trail-experience-admin__card--approved]="experience.status === 'APPROVED'"
                            [class.trail-experience-admin__card--denied]="experience.status === 'DENIED'"
                        >
                            <div class="trail-experience-admin__card-header">
                                <div class="trail-experience-admin__card-title-row">
                                    <span class="trail-experience-admin__student-name">{{
                                        experience.contactName ?? "Unknown"
                                    }}</span>
                                    <span
                                        class="trail-experience-admin__status-badge"
                                        [class.trail-experience-admin__status-badge--pending]="
                                            experience.status === 'PENDING'
                                        "
                                        [class.trail-experience-admin__status-badge--approved]="
                                            experience.status === 'APPROVED'
                                        "
                                        [class.trail-experience-admin__status-badge--denied]="
                                            experience.status === 'DENIED'
                                        "
                                    >
                                        {{ statusLabel(experience.status) }}
                                    </span>
                                </div>

                                @if (experience.curriculumName || experience.trailName || experience.rootName) {
                                    <div class="trail-experience-admin__meta">
                                        @if (experience.curriculumName) {
                                            <span>{{ experience.curriculumName }}</span>
                                        }
                                        @if (experience.curriculumName && experience.trailName) {
                                            <span> · </span>
                                        }
                                        @if (experience.trailName) {
                                            <span>{{ experience.trailName }}</span>
                                        }
                                        @if (experience.rootName) {
                                            <span class="trail-experience-admin__root-label">
                                                · Tree: {{ experience.rootName
                                                }}{{
                                                    experience.rootCode ? " (" + experience.rootCode + ")" : ""
                                                }}</span
                                            >
                                        }
                                    </div>
                                }
                            </div>

                            <h4 class="trail-experience-admin__experience-title">{{ experience.title }}</h4>
                            <p class="trail-experience-admin__description">{{ experience.description }}</p>

                            <div class="trail-experience-admin__dates">
                                <span>Experience: {{ formatDate(experience.experienceDt) }}</span>
                                <span> · Submitted: {{ formatDateTime(experience.submittedDt) }}</span>
                            </div>

                            @if (experience.status === "APPROVED" && experience.approvalReason) {
                                <div class="trail-experience-admin__review-info">
                                    <strong>{{ labels.APPROVAL_REASON }}:</strong> {{ experience.approvalReason }}
                                    @if (experience.reviewerName) {
                                        <span class="trail-experience-admin__reviewer">
                                            — {{ experience.reviewerName }}</span
                                        >
                                    }
                                </div>
                            }

                            @if (experience.status === "DENIED" && experience.denialReason) {
                                <div
                                    class="trail-experience-admin__review-info trail-experience-admin__review-info--denied"
                                >
                                    <strong>{{ labels.DENIAL_REASON }}:</strong> {{ experience.denialReason }}
                                    @if (experience.reviewerName) {
                                        <span class="trail-experience-admin__reviewer">
                                            — {{ experience.reviewerName }}</span
                                        >
                                    }
                                </div>
                            }

                            @if (experience.status === "PENDING") {
                                @if (activeAction()?.experienceId === experience.experienceId) {
                                    <div class="trail-experience-admin__action-form">
                                        <label class="trail-experience-admin__action-label">
                                            @if (activeAction()?.type === "approve") {
                                                {{ labels.APPROVAL_REASON }} (optional):
                                            } @else {
                                                {{ labels.DENIAL_REASON }} *:
                                            }
                                        </label>
                                        <textarea
                                            class="c-input trail-experience-admin__reason-input"
                                            rows="3"
                                            [ngModel]="actionReason()"
                                            (ngModelChange)="actionReason.set($event)"
                                            [placeholder]="
                                                activeAction()?.type === 'approve'
                                                    ? 'Optional reason for approval'
                                                    : 'Reason for denial (required)'
                                            "
                                        ></textarea>
                                        <div class="trail-experience-admin__action-buttons">
                                            <s25-ng-button
                                                [type]="activeAction()?.type === 'approve' ? 'primary' : 'danger'"
                                                [disabled]="
                                                    actionProcessing() ||
                                                    (activeAction()?.type === 'deny' && !actionReason().trim())
                                                "
                                                [onClick]="submitActionFactory(experience)"
                                            >
                                                @if (actionProcessing()) {
                                                    Processing…
                                                } @else if (activeAction()?.type === "approve") {
                                                    Confirm {{ labels.APPROVE }}
                                                } @else {
                                                    Confirm {{ labels.DENY }}
                                                }
                                            </s25-ng-button>
                                            <s25-ng-button [type]="'danger--outline'" [onClick]="cancelAction">
                                                Cancel
                                            </s25-ng-button>
                                        </div>
                                    </div>
                                } @else {
                                    <div class="trail-experience-admin__actions">
                                        <s25-ng-button [type]="'primary'" [onClick]="startApproveFactory(experience)">
                                            {{ labels.APPROVE }}
                                        </s25-ng-button>
                                        <s25-ng-button [type]="'danger'" [onClick]="startDenyFactory(experience)">
                                            {{ labels.DENY }}
                                        </s25-ng-button>
                                    </div>
                                }
                            }
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .trail-experience-admin__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }
        .trail-experience-admin__header h1 {
            margin: 0;
        }
        .trail-experience-admin__filters {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 1rem;
        }
        .trail-experience-admin__filter {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .trail-experience-admin__filter select {
            min-width: 12rem;
        }
        .trail-experience-admin__cards {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }
        .trail-experience-admin__card {
            border: 1px solid var(--color--border);
            border-left: 4px solid var(--color--border);
            border-radius: 8px;
            padding: 1rem 1.25rem;
            background: var(--color--surface-2-bg);
        }
        .trail-experience-admin__card--pending {
            border-left-color: #f0ad4e;
        }
        .trail-experience-admin__card--approved {
            border-left-color: #28a745;
        }
        .trail-experience-admin__card--denied {
            border-left-color: #dc3545;
            background: #fff5f5;
        }
        .trail-experience-admin__card-header {
            margin-bottom: 0.5rem;
        }
        .trail-experience-admin__card-title-row {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
        }
        .trail-experience-admin__student-name {
            font-weight: 600;
            font-size: 1rem;
        }
        .trail-experience-admin__status-badge {
            display: inline-block;
            padding: 0.15rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
        }
        .trail-experience-admin__status-badge--pending {
            background: #fff3cd;
            color: #856404;
        }
        .trail-experience-admin__status-badge--approved {
            background: #d4edda;
            color: #155724;
        }
        .trail-experience-admin__status-badge--denied {
            background: #f8d7da;
            color: #721c24;
        }
        .trail-experience-admin__meta {
            font-size: 0.85rem;
            color: var(--color--text-muted);
            margin-top: 0.2rem;
        }
        .trail-experience-admin__experience-title {
            margin: 0.25rem 0;
            font-size: 0.95rem;
        }
        .trail-experience-admin__description {
            margin: 0 0 0.5rem;
            font-size: 0.9rem;
            line-height: 1.4;
        }
        .trail-experience-admin__dates {
            font-size: 0.82rem;
            color: var(--color--text-muted);
            margin-bottom: 0.5rem;
        }
        .trail-experience-admin__review-info {
            padding: 0.5rem 0.75rem;
            background: #f0faf0;
            border-radius: 4px;
            font-size: 0.85rem;
            margin-bottom: 0.5rem;
        }
        .trail-experience-admin__review-info--denied {
            background: #fef0f0;
        }
        .trail-experience-admin__reviewer {
            font-size: 0.8rem;
            color: var(--color--text-muted);
        }
        .trail-experience-admin__actions {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.5rem;
        }
        .trail-experience-admin__action-form {
            margin-top: 0.75rem;
            padding: 0.75rem;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
        }
        .trail-experience-admin__action-label {
            display: block;
            font-size: 0.85rem;
            font-weight: 600;
            margin-bottom: 0.35rem;
        }
        .trail-experience-admin__reason-input {
            width: 100%;
            resize: vertical;
            margin-bottom: 0.5rem;
        }
        .trail-experience-admin__action-buttons {
            display: flex;
            gap: 0.5rem;
        }
    `,
})
export class TrailExperienceAdminComponent {
    readonly labels = TrailLabels;
    private readonly userStore = inject(UserStore);

    handleBack = () => {
        void StateService.go("seTrailAdmin", null, null);
    };

    formatDate(value: string | undefined | null): string {
        return formatUserprefDate(value, this.userStore.dateFormat());
    }

    formatDateTime(value: string | undefined | null): string {
        return formatUserprefDate(value, this.userStore.dateTimeFormat());
    }

    readonly statusFilter = signal<StatusFilter>("PENDING");
    readonly selectedCohortId = signal<number | null>(null);
    readonly selectedDefnId = linkedSignal<number | null>(() => {
        this.selectedCohortId();
        return null;
    });
    private readonly reloadGen = signal(0);

    readonly activeAction = signal<{ experienceId: number; type: "approve" | "deny" } | null>(null);
    readonly actionReason = signal("");
    readonly actionProcessing = signal(false);

    readonly cohorts = s25Resource({
        params: () => this.reloadGen(),
        loader: () => TrailApi.getCohorts(),
    });

    readonly curricula = s25Resource({
        if: () => this.selectedCohortId() != null,
        params: () => this.selectedCohortId()!,
        loader: ({ params: cohortId }) => TrailApi.listCurricula(cohortId),
    });

    readonly experiencesRes = s25Resource({
        if: () => this.selectedCohortId() != null,
        params: () => ({
            status: this.statusFilter(),
            cohortId: this.selectedCohortId(),
            defnId: this.selectedDefnId(),
            reload: this.reloadGen(),
        }),
        loader: ({ params }) =>
            TrailApi.getAdminExperiences({
                status: params.status === "" ? undefined : params.status,
                cohortId: params.cohortId ?? undefined,
                defnId: params.defnId ?? undefined,
            }),
    });

    readonly experiences = computed((): ExperienceListItem[] => this.experiencesRes.value() ?? []);

    refreshList = () => {
        this.reloadGen.update((count) => count + 1);
    };

    statusLabel(status: string): string {
        switch (status) {
            case "PENDING":
                return TrailLabels.APPROVAL_PENDING;
            case "APPROVED":
                return TrailLabels.APPROVED;
            case "DENIED":
                return TrailLabels.DENIED;
            default:
                return status;
        }
    }

    startApproveFactory = (experience: ExperienceListItem) => {
        return () => {
            this.activeAction.set({ experienceId: experience.experienceId, type: "approve" });
            this.actionReason.set("");
        };
    };

    startDenyFactory = (experience: ExperienceListItem) => {
        return () => {
            this.activeAction.set({ experienceId: experience.experienceId, type: "deny" });
            this.actionReason.set("");
        };
    };

    cancelAction = () => {
        this.activeAction.set(null);
        this.actionReason.set("");
    };

    submitActionFactory = (experience: ExperienceListItem) => {
        return async () => {
            const action = this.activeAction();
            if (!action) return;

            if (action.type === "deny" && !this.actionReason().trim()) return;

            if (action.type === "deny") {
                const confirmed = await AlertService.confirm(
                    `Deny this experience from ${experience.contactName ?? "this student"}?`,
                );
                if (!confirmed) return;
            }

            this.actionProcessing.set(true);
            try {
                if (action.type === "approve") {
                    await TrailApi.approveExperience(experience.experienceId, this.actionReason().trim() || undefined);
                } else {
                    await TrailApi.denyExperience(experience.experienceId, this.actionReason().trim());
                }
                this.activeAction.set(null);
                this.actionReason.set("");
                this.reloadGen.update((count) => count + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            } finally {
                this.actionProcessing.set(false);
            }
        };
    };
}
